import { useGetVoteQuestions } from '../api/getVoteQuestions';
import MainContainer from '@/components/Shared/MainContainer';
import { PageWrapper } from '@/components/Shared/Wrapper';
import { useSendVote } from '../api/sendVoteAnswer';
import { StyledForm } from '@/components/Shared/Form';
import { App, Flex, Form, Radio, Typography } from 'antd';
import themeToken from '@lib/theme/tokens/index';
import { StyledButton } from '@/lib/theme/components/Button';
import { useTranslation } from 'react-i18next';
import { useSelectedCompanyStore } from '@/stores/selectedCompany';
import styled from 'styled-components';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useGetMeetingDetails } from '@/features/investorRelations/api/meetings/getMeetingDetails';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import { useUserDataStore } from '@/stores/UserData';
import { handleChangeLanguage } from '@/utils/handleChangeLanguage';

const { Text } = Typography;

const CustomRadio = styled(Radio)`
  font-size: 22px;
`;

const Vote = () => {
  const { selectedCompany } = useSelectedCompanyStore((state) => state);
  const { userData } = useUserDataStore((state) => state);
  const navigate = useNavigate();
  const { notification } = App.useApp();
  const hasRedirectedRef = useRef(false);
  const { meetingID } = useParams<{ meetingID: string }>();
  const isPublicVoteLink = window.location.pathname.includes('/public');

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');

  const { data: meetingDetails, isLoading: isGetMeetingDetailsLoading } = useGetMeetingDetails({
    companyID: selectedCompany?.cid as number,
    meetingID: meetingID ? +meetingID : -1,
  });

  const meetingStartTime = meetingDetails?.data?.vote_start_date ? dayjs(meetingDetails.data.vote_start_date) : null;
  const isMeetingNotStarted = Boolean(
    meetingStartTime && new Date(meetingStartTime.toString()).getTime() >= Date.now(),
  );

  useEffect(() => {
    if (isGetMeetingDetailsLoading || hasRedirectedRef.current) return;

    if (isMeetingNotStarted) {
      hasRedirectedRef.current = true;
      notification.error({
        message: 'Voting is not available yet',
        description: meetingStartTime
          ? `Voting opens at ${meetingStartTime.format('MMM D, YYYY, h:mm A')}`
          : 'The meeting has not started yet.',
      });
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/investor-relations/meetings', { replace: true });
      }
    }
  }, [isGetMeetingDetailsLoading, isMeetingNotStarted, meetingStartTime, notification, navigate]);

  const { data: voteQuestions } = useGetVoteQuestions({
    urlParams: { page: 1, token: token ?? undefined },
    companyID: selectedCompany?.cid as number,
    meetingID: +(meetingID ?? -1),
    meetingAttendeeID: token ? +(searchParams.get('user_id') ?? -1) : undefined,
  });

  const { t } = useTranslation('vote', {
    keyPrefix: 'vote',
  });

  const [form] = Form.useForm();

  const { mutate: sendVote, isPending: isSendingVote } = useSendVote({
    handleAfterFailure: () => {
      console.error('Error sending vote:');
      // Optionally, you can handle the error here
    },
    postSuccess: () => {
      form.resetFields();
    },
  });

  const handleVoteSubmit = () => {
    const _vote = form.getFieldValue('vote') as string;
    const payload = isPublicVoteLink
      ? {
          voteID: voteQuestions?.data[0]?.id || 0,
          meetingID: +(meetingID ?? -1),
          token: token ?? '',
          body: {
            vote: _vote,
          },
        }
      : {
          voteID: voteQuestions?.data[0]?.id || 0,
          companyID: selectedCompany?.cid as number,
          meetingID: +(meetingID ?? -1),
          body: {
            vote: _vote,
          },
        };

    if (voteQuestions?.data[0]?.can_user_vote === false) {
      payload.body.vote = 'excluded';
    }

    sendVote(payload);
  };

  useEffect(() => {
    if (userData && isPublicVoteLink) {
      handleChangeLanguage(userData?.userInfo.locale);
    }
  }, [userData, isPublicVoteLink]);

  return (
    <>
      <MainContainer title={t('vote')} breadcrumbItems={[]}>
        <PageWrapper>
          {voteQuestions?.metadata.total && voteQuestions?.metadata.total > 0 ? (
            <Flex vertical>
              <Text
                style={{
                  marginBottom: '16px',
                  fontSize: '22px',
                  fontWeight: '700',
                  color: themeToken['branding-primary-6'],
                }}
              >
                {t('clause')} {voteQuestions?.data[0].clause}
              </Text>
              <StyledForm
                form={form}
                onFinish={handleVoteSubmit}
                layout="vertical"
                style={{
                  padding: `30px`,
                  borderRadius: `${themeToken.borderRadiusLG}px`,
                  border: `1px solid ${themeToken['branding-natural-3']}`,
                  flex: voteQuestions?.metadata.total && voteQuestions?.metadata.total > 0 ? 0 : 1,
                }}
              >
                <Text
                  style={{
                    marginBottom: '18px',
                    fontSize: '20px',
                  }}
                >
                  {voteQuestions?.data[0]?.points_description}
                </Text>
                <Form.Item required name="vote">
                  <Radio.Group disabled={!voteQuestions?.data[0]?.can_user_vote}>
                    <CustomRadio value="yes">
                      {voteQuestions?.data[0]?.points_description ===
                      'التصويت على تعيين مراجع حسابات للشركة للسنة المالية 2026 وتحديد أتعابه من بين المرشحين بناء على توصية لجنة المراجعة لمجلس الادارة.'
                        ? 'PKF'
                        : t('yes')}
                    </CustomRadio>
                    <CustomRadio value="no">
                      {voteQuestions?.data[0]?.points_description ===
                      'التصويت على تعيين مراجع حسابات للشركة للسنة المالية 2026 وتحديد أتعابه من بين المرشحين بناء على توصية لجنة المراجعة لمجلس الادارة.'
                        ? 'RSM'
                        : t('no')}
                    </CustomRadio>
                    <CustomRadio value="no vote">{t('noVote')}</CustomRadio>
                  </Radio.Group>
                </Form.Item>
                {voteQuestions?.data[0]?.can_user_vote ? null : (
                  <Text
                    style={{
                      color: 'red',
                      fontSize: '22px',
                    }}
                  >
                    {t('excludedUser')}
                  </Text>
                )}
                <Flex justify="end">
                  <StyledButton
                    disabled={isSendingVote || isGetMeetingDetailsLoading}
                    loading={isSendingVote}
                    type="primary"
                    size="large"
                    htmlType="submit"
                  >
                    {voteQuestions?.data[0]?.can_user_vote ? t('submit') : t('next')}
                  </StyledButton>
                </Flex>
              </StyledForm>
            </Flex>
          ) : (
            <Flex vertical align="center" justify="center" flex={1}>
              {/* <Divider /> */}
              <h2>{t('noVoteQuestionsAvailable')}</h2>
            </Flex>
          )}
        </PageWrapper>
      </MainContainer>
    </>
  );
};

export default Vote;
